// TODO: memoization is broken in this file.
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import {
  ReusableComponentsVehicleModeEnum,
  RouteWithJourneyPatternStopsFragment,
  useGetRoutesWithStopsQuery,
} from '../../../../generated/graphql';
import {
  selectEditedRouteIncludedStops,
  selectMapRouteEditor,
  selectSelectedStopAreaId,
  selectSelectedStopId,
  useAppSelector,
} from '../../../../redux';
import { Priority } from '../../../../types/enums';
import { StopPlaceState } from '../../../../types/stop-registry';
import {
  filterHighestPriorityCurrentStops,
  isCurrentEntity,
} from '../../../../utils';
import { getRouteStopLabels } from '../../Routes';
import { MapStop } from '../../Types';
import { useMapObservationDate } from '../../Utils/mapUrlState';

function extractHighestPriorityStopsFromRoute<
  TRoute extends RouteWithJourneyPatternStopsFragment,
>(route: TRoute, observationDate: DateTime) {
  const routeStopPoints =
    route.route_journey_patterns[0].ordered_scheduled_stop_point_in_journey_patterns.flatMap(
      (journeyPatternStop) => journeyPatternStop.scheduled_stop_points,
    ) ?? [];

  return filterHighestPriorityCurrentStops(
    routeStopPoints,
    observationDate,
    // If the route is a draft, we want to select draft versions of stops if there are any
    route.priority === Priority.Draft,
  );
}

export function useMapStops(displayedRouteIds: ReadonlyArray<string>) {
  const { selectedRouteId } = useAppSelector(selectMapRouteEditor);
  const selectedStopAreaId = useAppSelector(selectSelectedStopAreaId);
  const observationDate = useMapObservationDate();
  const selectedStopId = useAppSelector(selectSelectedStopId);
  const editedRouteIncludedStops = useAppSelector(
    selectEditedRouteIncludedStops,
  );

  const displayedRoutesResult = useGetRoutesWithStopsQuery({
    variables: { routeFilters: { route_id: { _in: displayedRouteIds } } },
  });

  const displayedRoutes = useMemo(
    () => displayedRoutesResult.data?.route_route ?? [],
    [displayedRoutesResult],
  );

  const selectedRoute = displayedRoutes.find(
    (route) => route.route_id === selectedRouteId,
  );

  const selectedRouteActiveStops = selectedRoute
    ? extractHighestPriorityStopsFromRoute(selectedRoute, observationDate)
    : [];

  const selectedRouteActiveStopIds = selectedRouteActiveStops.map(
    (stop) => stop.stop_place_ref,
  );

  const editedRouteStopIds = editedRouteIncludedStops.map(
    (stop) => stop.stop_place_ref,
  );

  const stopLabelToVehicleMode: Readonly<
    Record<string, ReusableComponentsVehicleModeEnum>
  > = useMemo(() => {
    const labelsToModes: Record<string, ReusableComponentsVehicleModeEnum> = {};

    displayedRoutes.forEach((route) => {
      const vehicleMode = route.route_line.primary_vehicle_mode;
      getRouteStopLabels(route).forEach((label) => {
        if (!(label in labelsToModes)) {
          labelsToModes[label] = vehicleMode;
        }
      });
    });

    return labelsToModes;
  }, [displayedRoutes]);

  const usedStopLabels: ReadonlyArray<string> = useMemo(
    () => [
      ...editedRouteIncludedStops
        .map((stop) => stop.label)
        .filter((label): label is string => !!label),
      ...Object.keys(stopLabelToVehicleMode),
    ],
    [editedRouteIncludedStops, stopLabelToVehicleMode],
  );

  const getStopShouldBeGray = useCallback(
    (stop: MapStop): boolean =>
      (!!selectedRouteId && !usedStopLabels.includes(stop.label)) ||
      !isCurrentEntity(observationDate, stop) ||
      stop.stop_state !== StopPlaceState.InOperation,
    [selectedRouteId, observationDate, usedStopLabels],
  );

  // If editing a route, highlight stops on edited route
  // Otherwise highlight stops belonging to selected route
  // Also highlight selectedRoute stops until editedRouteData is loaded
  const highlightedStopIds =
    editedRouteIncludedStops.length > 0
      ? editedRouteStopIds
      : selectedRouteActiveStopIds;
  const getStopHighlighted = useCallback(
    (stop: MapStop): boolean =>
      selectedStopAreaId === stop.stop_place_netex_id ||
      selectedStopId === stop.netex_id ||
      highlightedStopIds.includes(stop.netex_id),
    [highlightedStopIds, selectedStopId, selectedStopAreaId],
  );

  return { getStopHighlighted, getStopShouldBeGray };
}
